/**
 * LifeThemeIdentifierPage
 * Huvudsida för Life Theme Identifier
 * Visar introduktion och karusell med 5 obesvarade frågor
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QuestionCarousel from '../components/LifeThemeIdentifier/QuestionCarousel';

const LifeThemeIdentifierPage = () => {
  const navigate = useNavigate();
  const [showHelpModal, setShowHelpModal] = useState(false);

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Identifiera dina livsteman
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Förstå dina destruktiva mönster för att kunna förändra ditt liv.
          </p>
        </div>

        {/* Question Carousel */}
        <QuestionCarousel onHelpClick={() => setShowHelpModal(true)} />
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Hjälp & Information
              </h2>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Info Cards */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Om Livsteman</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      20 Livsteman
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Utforska alla 20 livsteman enligt Jeffrey Youngs schematerapi-modell
                    </p>
                  </div>

                  <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      116 Frågor
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Besvara frågor i din egen takt. Dina svar sparas automatiskt.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                      <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Personliga Insikter
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Få djupare förståelse för dina mönster och hur de påverkar dig
                    </p>
                  </div>
                </div>
              </div>

              {/* How It Works */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Så fungerar det</h3>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-3">
                      1
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Välj en fråga
                    </h4>
                    <p className="text-sm text-gray-600">
                      Klicka på en fråga från karusellen nedan
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-3">
                      2
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Svara ärligt
                    </h4>
                    <p className="text-sm text-gray-600">
                      Välj det alternativ (1-6) som bäst beskriver dig
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-3">
                      3
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Fortsätt inom temat
                    </h4>
                    <p className="text-sm text-gray-600">
                      Besvara alla frågor i livstemat du valt
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-3">
                      4
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Se dina resultat
                    </h4>
                    <p className="text-sm text-gray-600">
                      Få insikter när du slutfört ett tema
                    </p>
                  </div>
                </div>
              </div>

              {/* Tips Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">
                      Tips för bästa resultat
                    </h4>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Svara så ärligt som möjligt - det finns inga rätt eller fel svar</li>
                      <li>• Tänk på hur du känner i allmänhet, inte bara i nuet</li>
                      <li>• Ta din tid och fundera över varje fråga</li>
                      <li>• Du kan alltid komma tillbaka och ändra dina svar senare</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifeThemeIdentifierPage;
